import { CollectionState } from "@redotech/congo-enums/collection-state";
import {
  type ProviderAccountSource,
  ShippingProviderType,
  shippingProviderTypeOf,
} from "@redotech/redo-fulfillment-carrier-common/shipment";

/** Whose carrier account booked a collection; cancel and tracking must use the same one. */
// Same values as the unrelated shipping_claim_source DB enum. //redontIgnore
export enum CollectionAccountSource {
  MERCHANT = "merchant",
  REDO = "redo",
}

export interface CollectionAccountRef {
  source: CollectionAccountSource;
  /** The merchant carrier account; absent for Redo's own account. */
  carrierAccountId?: string | null;
}

export function collectionAccountRefOf(rate: {
  carrier_account_id?: string | null;
  is_merchant_carrier_account?: boolean | null;
}): CollectionAccountRef {
  return rate.is_merchant_carrier_account
    ? {
        source: CollectionAccountSource.MERCHANT,
        carrierAccountId: rate.carrier_account_id,
      }
    : { source: CollectionAccountSource.REDO };
}

export interface CollectionCapabilities {
  supportsCancel: boolean;
  supportsReschedule: boolean;
  requiresPhone: boolean;
}

export interface CollectionTimeOfDay {
  hour: number;
  minute: number;
}

/** The provider-local times every collection is booked between. */
export interface CollectionWindow {
  ready: CollectionTimeOfDay;
  close: CollectionTimeOfDay;
}

interface CollectionProviderDefinition {
  displayName: string;
  capabilities: CollectionCapabilities;
  window: CollectionWindow;
}

/** DX Freight's collection rules, shared with its scheduler. */
export const DX_FREIGHT_COLLECTION_CAPABILITIES: CollectionCapabilities = {
  supportsCancel: true,
  supportsReschedule: false,
  requiresPhone: true,
};

export const DX_FREIGHT_COLLECTION_WINDOW: CollectionWindow = {
  ready: { hour: 8, minute: 0 },
  close: { hour: 18, minute: 0 },
};

/**
 * Carriers that collect a return from the customer's address instead of
 * printing a label. A carrier is listed once its shipping provider implements
 * CollectionCapability.
 */
export type CollectionCarrier = ShippingProviderType.DX_FREIGHT;

const collectionProviders: Record<
  CollectionCarrier,
  CollectionProviderDefinition
> = {
  [ShippingProviderType.DX_FREIGHT]: {
    displayName: "DX Freight",
    capabilities: DX_FREIGHT_COLLECTION_CAPABILITIES,
    window: DX_FREIGHT_COLLECTION_WINDOW,
  },
};

export function isCollectionCarrier(
  provider: string,
): provider is CollectionCarrier {
  return Object.hasOwn(collectionProviders, provider);
}

export function getCollectionProviderDisplayName(
  provider: CollectionCarrier,
): string {
  return collectionProviders[provider].displayName;
}

export function getCollectionCapabilities(
  provider: CollectionCarrier,
): CollectionCapabilities {
  return collectionProviders[provider].capabilities;
}

export function getCollectionWindow(
  provider: CollectionCarrier,
): CollectionWindow {
  return collectionProviders[provider].window;
}

/** The carrier behind a rate or shipment, if it collects rather than labels. */
export function collectionProviderOf(
  source: ProviderAccountSource | null | undefined,
): CollectionCarrier | undefined {
  const providerType = shippingProviderTypeOf(source);
  return providerType && isCollectionCarrier(providerType)
    ? providerType
    : undefined;
}

export function isCollectionShipment(
  source: ProviderAccountSource | null | undefined,
): boolean {
  return collectionProviderOf(source) !== undefined;
}

/** A return's collection for a shipment group, or its only one when no group is given. */
export function collectionForShipmentGroup<
  T extends { shipmentGroupId: string },
>(
  collections: readonly T[] | null | undefined,
  shipmentGroupId: string | null | undefined,
): T | undefined {
  if (shipmentGroupId) {
    return collections?.find(
      (collection) => collection.shipmentGroupId === shipmentGroupId,
    );
  }
  return collections?.length === 1 ? collections[0] : undefined;
}

/**
 * A collection provider the flow selected that can carry the shipment is its
 * only option, so a cheaper parcel rate can't win. When it can't (wrong lane,
 * account off) it returns no rates and the other selected sources apply.
 */
export function applyCollectionPrecedence<T extends ProviderAccountSource>(
  rates: T[],
): T[] {
  const collectionRates = rates.filter(isCollectionShipment);
  return collectionRates.length > 0 ? collectionRates : rates;
}

/** Collection states in which the customer still has nothing to ship. */
export function isCollectionPending(state: CollectionState): boolean {
  return (
    state === CollectionState.Requested ||
    state === CollectionState.Booking ||
    state === CollectionState.Booked ||
    // The provider may have booked it and may still collect.
    state === CollectionState.Unconfirmed
  );
}

/** States in which the provider has, or may have, a booking, so no label may replace it. */
export const COLLECTION_STATES_BLOCKING_LABELS: readonly CollectionState[] = [
  CollectionState.Requested,
  CollectionState.Booking,
  CollectionState.Booked,
  CollectionState.Unconfirmed,
];

/** What a collection keeps from its provider. */
export interface CollectionProviderData {
  bookingRef?: string;
  /** When a booking attempt claimed the collection, as an ISO instant; cleared once the attempt settles it. */
  claimedAt?: string;
  /** References of earlier bookings the provider never confirmed; any may exist as a duplicate. */
  unconfirmedReferences?: string[];
}

export function collectionProviderDataOf(
  providerData: unknown,
): CollectionProviderData {
  if (typeof providerData !== "object" || providerData === null) {
    return {};
  }
  const bookingRef =
    "bookingRef" in providerData &&
    typeof providerData.bookingRef === "string" &&
    providerData.bookingRef
      ? providerData.bookingRef
      : undefined;
  const claimedAt =
    "claimedAt" in providerData &&
    typeof providerData.claimedAt === "string" &&
    providerData.claimedAt
      ? providerData.claimedAt
      : undefined;
  const unconfirmedReferences =
    "unconfirmedReferences" in providerData &&
    Array.isArray(providerData.unconfirmedReferences)
      ? providerData.unconfirmedReferences.filter(
          (reference): reference is string =>
            typeof reference === "string" && reference.length > 0,
        )
      : [];
  // Keys in one fixed order: a claim is matched by its whole providerData, and Mongo compares in key order.
  return {
    ...(bookingRef && { bookingRef }),
    ...(claimedAt && { claimedAt }),
    ...(unconfirmedReferences.length > 0 && { unconfirmedReferences }),
  };
}

/** Collection returns are carried by the provider, so label reminders and expiry never apply. */
export function hasCarrierCollection(return_: {
  carrierCollections?: readonly unknown[] | null;
}): boolean {
  return (return_.carrierCollections?.length ?? 0) > 0;
}
